"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.BusPortfolioMetricsSchema = exports.DelinquencyBucketsSchema = exports.ReconciledBusTapeResultSchema = exports.ReconciledMsrValuesSchema = exports.ReconcileCheckSchema = exports.ReconcileCheckStatusSchema = exports.ReconcileStatusSchema = exports.ReconciledMsrRowSchema = exports.ReconciledDataTapeRowArraySchema = exports.ReconciledDataTapeRowSchema = exports.ReconciledDataTapeRequiredRowSchema = void 0;
const zod_1 = require("zod");
/**
 * Backup Servicing (BUS) reconcile step — canonical schemas.
 *
 * Single source of truth for the reconcile step's inputs and output, shared by:
 *  - the `createReconciledBusTape` Fargate task (parses the sanitized tapes at
 *    runtime, and builds/validates the result JSON before upload), and
 *  - the `reconciled-bus-tape` tape-builder uinode (derives the required input
 *    column names from these schemas' keys for graph-author-time validation).
 *
 * Keeping the canonical column names as the object keys here means the
 * author-time column check and the runtime parse can never drift apart.
 */
/**
 * A tape value that must be numeric. Sanitized tape cells arrive as either a
 * number or a numeric string; a missing (null/undefined) or non-numeric value
 * is a hard error (surfaced by the parse) rather than a silent zero.
 */
const numericTapeField = zod_1.z
    .union([zod_1.z.number(), zod_1.z.string()])
    .transform((value, ctx) => {
    const parsed = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(parsed)) {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            message: `expected a numeric value, got "${String(value)}"`,
        });
        return zod_1.z.NEVER;
    }
    return parsed;
});
/**
 * A tape value that must be present and is read as a string (dates arrive as
 * either an ISO string or a serial number depending on the source file).
 */
const stringTapeField = zod_1.z
    .union([zod_1.z.string(), zod_1.z.number()])
    .transform((value) => String(value));
/**
 * A LENIENT numeric MSR cell: a blank (null / empty) or non-numeric value maps
 * to `null` (→ a skipped check) instead of erroring. The MSR is a fixed-layout
 * form whose cells don't line up perfectly across every period, and the tie-out
 * requirements are still firming up, so a missing cell must NOT fail the run —
 * it's recorded as unverifiable. Contrast `numericTapeField` (data tape), where
 * a missing value is still a hard error.
 */
const nullableNumericTapeField = zod_1.z
    .union([zod_1.z.number(), zod_1.z.string()])
    .nullable()
    .transform((value) => {
    if (value === null || value === '') {
        return null;
    }
    const parsed = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(parsed) ? parsed : null;
});
/** A LENIENT string MSR cell: blank (null / empty) maps to `null`. */
const nullableStringTapeField = zod_1.z
    .union([zod_1.z.string(), zod_1.z.number()])
    .nullable()
    .transform((value) => (value === null || value === '' ? null : String(value)));
/**
 * A data-tape column reconcile reads ONLY to compute the portfolio metrics: an
 * absent column, a blank cell, or a non-numeric value maps to `null` instead of
 * erroring, and the derived metric degrades to `null` / the `unknown`
 * delinquency bucket.
 *
 * Deliberately lenient, unlike `numericTapeField`: these columns are not part of
 * reconcile's contract. A deal whose tape omits `remaining_term` must still
 * reconcile and certify — the portfolio snapshot itself carries a "Not
 * available" column for exactly that case — so a reporting nicety must never
 * become a run-blocking requirement across ~100 BUS clients. Note the key stays
 * REQUIRED and nullable in the parsed output; only the tape input may omit it.
 */
const metricsOnlyNumericTapeField = zod_1.z
    .union([zod_1.z.number(), zod_1.z.string()])
    .nullish()
    .transform((value) => {
    if (value === null || value === undefined || value === '') {
        return null;
    }
    const parsed = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(parsed) ? parsed : null;
});
// ── inputs ──────────────────────────────────────────────────────────────────
/**
 * The data-tape columns reconcile cannot work without — a missing or non-numeric
 * value is a hard error. These are also the columns a graph author MUST map:
 * the uinode derives its author-time required-column check from these keys.
 */
exports.ReconciledDataTapeRequiredRowSchema = zod_1.z.object({
    current_principal_bal: numericTapeField,
    as_of_date: stringTapeField,
});
/**
 * The columns that feed only the portfolio metrics. Separate from the required
 * set so a tape that omits them still reconciles — see
 * `metricsOnlyNumericTapeField`.
 */
const ReconciledDataTapeMetricsRowSchema = zod_1.z.object({
    term: metricsOnlyNumericTapeField,
    remaining_term: metricsOnlyNumericTapeField,
    num_days_delin: metricsOnlyNumericTapeField,
});
/** Per-row shape of the sanitized data tape (only the fields reconcile reads). */
exports.ReconciledDataTapeRowSchema = exports.ReconciledDataTapeRequiredRowSchema.merge(ReconciledDataTapeMetricsRowSchema);
exports.ReconciledDataTapeRowArraySchema = zod_1.z.array(exports.ReconciledDataTapeRowSchema);
/**
 * Single-row shape of the extracted MSR control values. Every cell is LENIENT
 * (nullable): a blank/garbage cell parses to `null` and downstream becomes a
 * skipped check, never a hard parse failure. See `nullableNumericTapeField`.
 */
exports.ReconciledMsrRowSchema = zod_1.z.object({
    collection_period_ending: nullableStringTapeField,
    beginning_pool_balance: nullableNumericTapeField,
    ending_pool_balance: nullableNumericTapeField,
    ending_pool_balance_footing: nullableNumericTapeField,
    servicing_fee: nullableNumericTapeField,
});
// ── output ──────────────────────────────────────────────────────────────────
exports.ReconcileStatusSchema = zod_1.z.enum(['pass', 'fail', 'skipped']);
/**
 * Outcome of a single tie-out check. `skipped` means the check could not run
 * (its MSR input cell was blank/absent) — distinct from `fail`. This keeps the
 * checks flexible while the reconcile requirements settle: everything is
 * surfaced (in the result JSON + the cycle row's `variances`), nothing errors.
 */
exports.ReconcileCheckStatusSchema = zod_1.z.enum(['pass', 'fail', 'skipped']);
exports.ReconcileCheckSchema = zod_1.z.object({
    name: zod_1.z.string(),
    status: exports.ReconcileCheckStatusSchema,
    detail: zod_1.z.string(),
});
/**
 * The MSR control values echoed into the result. `null` at the top level when no
 * MSR is wired; individual fields are also nullable (a blank cell in the form).
 */
exports.ReconciledMsrValuesSchema = zod_1.z.object({
    collectionPeriodEnding: zod_1.z.string().nullable(),
    beginningPoolBalance: zod_1.z.number().nullable(),
    endingPoolBalance: zod_1.z.number().nullable(),
    endingPoolBalanceFooting: zod_1.z.number().nullable(),
    servicingFee: zod_1.z.number().nullable(),
});
/**
 * The reconcile result JSON written to S3 and read by the certificate step.
 * `status` is `'skipped'` when no checks ran (no MSR tape wired) — distinct
 * from a `'pass'` where checks ran and all passed.
 *
 * `cycleId` back-references the `bus_servicing_cycle` DB row the reconcile task
 * upserts (the committed system-of-record the certificate reads). It is the
 * other half of a bidirectional link: the DB row holds this artifact's S3 key
 * (`reconcile_result_ref`) and this JSON holds the row id. Always set — the task
 * always upserts a cycle row and embeds its id — so it is a required non-null
 * string (the whole stack lands together, so no window exists where the schema
 * requires a key the task doesn't yet provide).
 */
exports.ReconciledBusTapeResultSchema = zod_1.z.object({
    status: exports.ReconcileStatusSchema,
    reportingPeriod: zod_1.z.string().nullable(),
    controlTotals: zod_1.z.object({
        loanCount: zod_1.z.number(),
        totalPrincipal: zod_1.z.number(),
    }),
    msrValues: exports.ReconciledMsrValuesSchema.nullable(),
    checks: zod_1.z.array(exports.ReconcileCheckSchema),
    provenance: zod_1.z.string(),
    cycleId: zod_1.z.string(),
});
// ── portfolio metrics ───────────────────────────────────────────────────────
const DelinquencyBucketSchema = zod_1.z.object({
    loanCount: zod_1.z.number(),
    /** Sum of `current_principal_bal` for the loans in this bucket. */
    principalBalance: zod_1.z.number(),
});
/**
 * One bucket per delinquency band, as verified against Concord's own Backup
 * Servicing Report (`docs/bus/reference/analysis-notes.md` — six bands with
 * `<=` boundaries). Both a count and a balance per band: the portfolio snapshot
 * presents balance-weighted percentages, the report presents counts, and holding
 * both is nearly free.
 *
 * `unknown` holds loans whose `num_days_delin` was absent or unparseable, so the
 * bands always sum back to the tape's total principal — including on a tape that
 * never carried the column at all, where everything lands here.
 *
 * Spelled out as an exhaustive object rather than a `Record<string, …>` so
 * adding a band is a compile error at every site that builds or reads one.
 */
exports.DelinquencyBucketsSchema = zod_1.z.object({
    current: DelinquencyBucketSchema,
    dpd1To10: DelinquencyBucketSchema,
    dpd11To30: DelinquencyBucketSchema,
    dpd31To60: DelinquencyBucketSchema,
    dpd61To90: DelinquencyBucketSchema,
    dpd91Plus: DelinquencyBucketSchema,
    unknown: DelinquencyBucketSchema,
});
/**
 * The reconcile-owned headline aggregate persisted on `bus_servicing_cycle`
 * (`portfolioMetrics` JSONB) and rendered by the portal's Portfolio and Deal
 * Overview pages — the rows of Scott's portfolio snapshot that come from the
 * tape. A materialized headline of the loan-level rows, not a second truth:
 * both derive from the same normalized tape. See
 * `docs/bus/abs-east-demo.md#where-the-numbers-live`.
 *
 * Average outstanding principal (snapshot row 11) is deliberately absent — it is
 * `totalPrincipal / loanCount`, derived at render time rather than stored twice.
 *
 * `schemaVersion` is stamped because this is a stored document that will grow:
 * a reader can tell an old row from a new one without probing for fields.
 */
exports.BusPortfolioMetricsSchema = zod_1.z.object({
    schemaVersion: zod_1.z.literal(1),
    loanCount: zod_1.z.number(),
    totalPrincipal: zod_1.z.number(),
    /** Mean `term`; null when no loan on the tape carried one. */
    averageOriginalTermMonths: zod_1.z.number().nullable(),
    /** Mean `remaining_term`; null when no loan on the tape carried one. */
    averageRemainingTermMonths: zod_1.z.number().nullable(),
    delinquency: exports.DelinquencyBucketsSchema,
});
//# sourceMappingURL=ReconciledBusTape.js.map